'use client';

import { useState, useEffect, createContext, useContext } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Lightbulb, Globe2, Mic, Sparkles, Check, Shield } from 'lucide-react';
import StepIdea from './StepIdea';
import StepVoice from './StepVoice';
import StepLanguage from './StepLanguage';
import StepTheme from './StepTheme';
import StepReview from './StepReview';
import { SoftGlow } from '../ui/magic';
import { MagicalGenerationModal } from '../magical-generation-modal';
import { InsufficientCreditsModal } from '../insufficient-credits-modal';
import { PurchaseSuccessModal } from '../purchase-success-modal';
import type { AudioBookLanguage } from '@/lib/site-config';

type Step = 'idea' | 'language' | 'voice';

interface WizardState {
  idea: string;
  voiceId: string;
  primaryLanguage: AudioBookLanguage;
  bilingualMode: boolean;
  secondaryLanguage: AudioBookLanguage;
  montessoriMode: boolean;
  pageCount?: number; // Admin-only: custom page count for YouTube videos
}

// 3 active steps + visual ✨ indicator
const STEPS: { id: Step; label: string; icon: string }[] = [
  { id: 'idea', label: 'Idea', icon: '💡' },
  { id: 'language', label: 'Language', icon: '🌍' },
  { id: 'voice', label: 'Voice', icon: '🎤' },
];

const GENERATE_ICON = '✨'; // Visual indicator only

export default function CreateWizard() {
  const { data: session } = useSession();
  const router = useRouter();
  
  const [currentStep, setCurrentStep] = useState<Step>('idea');
  const [wizardState, setWizardState] = useState<WizardState>({
    idea: '',
    voiceId: '',
    primaryLanguage: 'en',
    bilingualMode: false,
    secondaryLanguage: 'es',
    montessoriMode: false,
    pageCount: undefined, // undefined = use default (12 pages)
  });
  
  const [credits, setCredits] = useState(0);
  const [creditsLoaded, setCreditsLoaded] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [voiceName, setVoiceName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [audioBookId, setAudioBookId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentStepLabel, setCurrentStepLabel] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState<{type: 'credits' | 'subscription'; amount: number} | null>(null);

  useEffect(() => {
    if (session?.user) {
      fetchUserData();
    } else {
      // For non-logged-in users, fetch voices and set credits to 0
      fetchVoicesForGuest();
    }
    
    // Restore wizard state after login/purchase
    const savedWizard = sessionStorage.getItem('pendingAudioBookWizard');
    if (savedWizard) {
      try {
        const parsed = JSON.parse(savedWizard);
        // Only restore if saved within last 30 minutes
        if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          // Merge saved state with defaults to handle missing fields
          setWizardState({
            idea: parsed.wizardState.idea || '',
            voiceId: parsed.wizardState.voiceId || '',
            primaryLanguage: parsed.wizardState.primaryLanguage || 'en',
            bilingualMode: parsed.wizardState.bilingualMode || false,
            secondaryLanguage: parsed.wizardState.secondaryLanguage || 'es',
            montessoriMode: parsed.wizardState.montessoriMode || false,
          });
          setCurrentStep(parsed.currentStep);
          
          // If user just logged in and has credits, show a message
          if (session?.user) {
            const checkCreditsAndProceed = async () => {
              const accountRes = await fetch('/api/account');
              if (accountRes.ok) {
                const data = await accountRes.json();
                if (data.credits >= 3) {
                  // User now has credits, they can proceed with generation
                  alert('Welcome back! You can now generate your audio picture book.');
                }
              }
            };
            checkCreditsAndProceed();
          }
        } else {
          // Clear expired saved state
          sessionStorage.removeItem('pendingAudioBookWizard');
        }
      } catch (e) {
        console.error('Failed to restore wizard state:', e);
        sessionStorage.removeItem('pendingAudioBookWizard');
      }
    }
  }, [session]);

  // Handle successful credit purchase
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const purchaseSuccess = urlParams.get('purchase');
    const purchaseType = urlParams.get('type');
    const purchaseAmount = urlParams.get('amount');
    
    if (purchaseSuccess === 'success' && session?.user && purchaseType && purchaseAmount) {
      // Refetch user data to get updated credits
      fetchUserData().then(() => {
        // Show success modal
        setPurchaseDetails({
          type: purchaseType as 'credits' | 'subscription',
          amount: parseInt(purchaseAmount, 10)
        });
        setShowPurchaseSuccess(true);
        
        // Restore wizard state from sessionStorage
        const savedWizard = sessionStorage.getItem('pendingAudioBookWizard');
        if (savedWizard) {
          try {
            const parsed = JSON.parse(savedWizard);
            if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
              // Merge saved state with defaults to handle missing fields
              setWizardState({
                idea: parsed.wizardState.idea || '',
                voiceId: parsed.wizardState.voiceId || '',
                primaryLanguage: parsed.wizardState.primaryLanguage || 'en',
                bilingualMode: parsed.wizardState.bilingualMode || false,
                secondaryLanguage: parsed.wizardState.secondaryLanguage || 'es',
                montessoriMode: parsed.wizardState.montessoriMode || false,
              });
              setCurrentStep(parsed.currentStep);
            }
            sessionStorage.removeItem('pendingAudioBookWizard');
          } catch (e) {
            console.error('Failed to restore wizard state:', e);
          }
        }
        
        // Clean up URL
        window.history.replaceState({}, '', '/audio-book/create');
      });
    }
  }, [session]);

  const fetchUserData = async () => {
    try {
      // Fetch account info (includes credits, subscription, etc.)
      const accountRes = await fetch('/api/account');
      if (accountRes.ok) {
        const data = await accountRes.json();
        setCredits(data.credits || 0);
        setIsSubscriber(data.isSubscriber || false);
        setIsPro(data.subscriptionPlan === 'AMAYA_PRO');
        
        // Fetch admin status from server-side API (environment variables aren't available client-side)
        const adminRes = await fetch('/api/admin/check');
        if (adminRes.ok) {
          const adminData = await adminRes.json();
          
          console.log('🔍 ADMIN CHECK DEBUG:');
          console.log('  - User Email:', adminData.email);
          console.log('  - Session Object:', session);
          console.log('  - isAdmin() Result:', adminData.isAdmin);
          console.log('  - Configured Admin Emails:', adminData.configuredEmails);
          console.log('  - Server Message:', adminData.message);
          
          setIsAdminUser(adminData.isAdmin);
          
          if (adminData.isAdmin) {
            console.log('✅ Admin user CONFIRMED:', adminData.email);
            console.log('  - You should see the admin badge and 50-page control');
          } else {
            console.log('❌ NOT an admin user:', adminData.email);
            console.log('  - Configured admins:', adminData.configuredEmails);
            console.log('  - Check if your email is in ADMIN_EMAILS environment variable');
            console.log('  - Email must match exactly (case-sensitive)');
          }
        } else {
          console.error('Failed to check admin status:', adminRes.status);
          setIsAdminUser(false);
        }
        
        setCreditsLoaded(true);
      } else {
        // If API fails, set defaults and mark as loaded
        console.error('Failed to fetch account:', accountRes.status);
        setCredits(0);
        setIsSubscriber(false);
        setIsPro(false);
        setCreditsLoaded(true);
      }

      // Fetch default voice
      const voicesRes = await fetch('/api/voices');
      if (voicesRes.ok) {
        const data = await voicesRes.json();
        const voices = data.voices || [];
        if (voices.length > 0 && !wizardState.voiceId) {
          setWizardState(prev => ({ ...prev, voiceId: voices[0].voiceId }));
          setVoiceName(voices[0].name);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Always set creditsLoaded to true, even on error
      setCredits(0);
      setIsSubscriber(false);
      setIsPro(false);
      setCreditsLoaded(true);
    }
  };

  const fetchVoicesForGuest = async () => {
    try {
      // Set credits to 0 for guest users and mark as loaded
      setCredits(0);
      setIsSubscriber(false);
      setIsPro(false);
      setCreditsLoaded(true);

      // Fetch default voice so guests can preview
      const voicesRes = await fetch('/api/voices');
      if (voicesRes.ok) {
        const data = await voicesRes.json();
        const voices = data.voices || [];
        if (voices.length > 0 && !wizardState.voiceId) {
          setWizardState(prev => ({ ...prev, voiceId: voices[0].voiceId }));
          setVoiceName(voices[0].name);
        }
      }
    } catch (error) {
      console.error('Error fetching voices for guest:', error);
      // Always set creditsLoaded to true, even on error
      setCredits(0);
      setIsSubscriber(false);
      setIsPro(false);
      setCreditsLoaded(true);
    }
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'idea':
        return wizardState.idea.trim().length > 0;
      case 'language':
        return wizardState.primaryLanguage.length > 0;
      case 'voice':
        return wizardState.voiceId.length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!canProceed()) return;
    
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    
    // If on the last step (Voice), auto-generate
    if (currentIndex === STEPS.length - 1) {
      handleSubmit();
      return;
    }
    
    // Otherwise move to next step
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleInspireMe = (composedPrompt: string) => {
    setWizardState(prev => ({ ...prev, idea: composedPrompt }));
    setCurrentStep('idea');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleSubmit = async () => {
    // Step 1: Check if user is logged in
    if (!session?.user) {
      // Save wizard state to sessionStorage so we can restore it after login
      sessionStorage.setItem('pendingAudioBookWizard', JSON.stringify({
        wizardState,
        currentStep: 'voice',
        timestamp: Date.now()
      }));
      
      // Redirect to login page with callback to this page
      const callbackUrl = encodeURIComponent(window.location.href);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    // Step 2: Check if user has credits (everyone needs credits, including subscribers)
    if (credits < 3) {
      // Save wizard state before showing modal
      sessionStorage.setItem('pendingAudioBookWizard', JSON.stringify({
        wizardState,
        currentStep: 'voice',
        timestamp: Date.now()
      }));
      
      // Show the beautiful insufficient credits modal
      setShowInsufficientCreditsModal(true);
      return;
    }

    // Step 3: Proceed with generation
    setGenerating(true);
    setProgress(0);
    setCurrentStepLabel('🎨 Bringing your story to life...');

    try {
      const response = await fetch('/api/audio-book/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: wizardState.idea,
          voiceId: wizardState.voiceId,
          language: wizardState.primaryLanguage,
          learnMode: wizardState.bilingualMode,
          sourceLanguage: wizardState.primaryLanguage,
          targetLanguage: wizardState.secondaryLanguage,
          montessoriMode: wizardState.montessoriMode,
          pageCount: wizardState.pageCount, // Admin-only: custom page count
          // Themes will be auto-generated from the prompt
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setAudioBookId(data.audioBookId);
        pollProgress(data.audioBookId);
        
        // Clear saved wizard state after successful generation
        sessionStorage.removeItem('pendingAudioBookWizard');
      } else {
        if (data.error === 'Insufficient credits') {
          setGenerating(false);
          setShowInsufficientCreditsModal(true);
        } else {
          throw new Error(data.error || 'Failed to generate audio book');
        }
      }
    } catch (error: any) {
      console.error('Generation error:', error);
      alert(error.message || 'Failed to generate audio book. Please try again.');
      setGenerating(false);
    }
  };

  const handlePurchase = async (packageType: string) => {
    setShowInsufficientCreditsModal(false);
    
    try {
      const res = await fetch('/api/checkout/credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageType }),
      });

      if (!res.ok) throw new Error('Checkout failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  const handleSubscribe = async (plan?: 'amaya_plus' | 'amaya_pro') => {
    setShowInsufficientCreditsModal(false);
    
    const subscriptionPlan = plan || 'amaya_plus';
    
    try {
      const res = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: subscriptionPlan }),
      });

      if (!res.ok) throw new Error('Subscription failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
    }
  };

  const pollProgress = (bookId: string) => {
    console.log('[CreateWizard] Starting progress polling for:', bookId);
    
    // Track progress simulation - smooth animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        // Slow down as we approach completion
        const increment = prev < 50 ? 2 : prev < 80 ? 1 : 0.5;
        const newProgress = Math.min(prev + increment, 97); // Cap at 97%, reach 100% only on completion
        return newProgress;
      });
    }, 1500); // Update every 1.5 seconds for smooth progress
    
    // Poll actual status from correct endpoint
    let pollCount = 0;
    const statusInterval = setInterval(async () => {
      pollCount++;
      try {
        // Use the correct status endpoint that doesn't require pagesData
        const res = await fetch(`/api/audio-book/status?id=${bookId}`);
        
        // Handle different response codes properly
        if (res.status === 404) {
          console.error('[CreateWizard] Audio book not found:', bookId);
          clearInterval(progressInterval);
          clearInterval(statusInterval);
          alert('Audio book not found. Please try again.');
          setGenerating(false);
          return;
        }
        
        if (res.status === 422) {
          // This is normal during generation - book exists but no pagesData yet
          console.log(`[CreateWizard] Poll #${pollCount}: Still generating (422 - no content yet)`);
          return; // Continue polling
        }
        
        if (!res.ok) {
          console.warn(`[CreateWizard] Poll #${pollCount}: Status ${res.status}`);
          return; // Continue polling
        }

        const data = await res.json();
        console.log(`[CreateWizard] Poll #${pollCount}: Status = ${data.status}`);

        if (data.status === 'completed') {
          console.log('[CreateWizard] ✅ Generation completed! Redirecting...');
          clearInterval(progressInterval);
          clearInterval(statusInterval);
          setProgress(100);
          setCurrentStepLabel('🎉 Your audio picture book is ready!');
          setTimeout(() => {
            router.push(`/audio-book/${bookId}`);
          }, 1000);
        } else if (data.status === 'failed') {
          console.error('[CreateWizard] ❌ Generation failed');
          clearInterval(progressInterval);
          clearInterval(statusInterval);
          alert('Generation failed. Please try again.');
          setGenerating(false);
        } else if (data.status === 'pending') {
          console.log(`[CreateWizard] Still pending (poll #${pollCount})...`);
          // Progress is already being updated by progressInterval
        }
      } catch (error) {
        console.error('[CreateWizard] Polling error:', error);
        // Don't stop polling on network errors, might be temporary
      }
    }, 3000); // Poll every 3 seconds

    // Cleanup after 10 minutes (extended timeout for longer generations)
    setTimeout(() => {
      console.warn('[CreateWizard] Polling timeout after 10 minutes');
      clearInterval(progressInterval);
      clearInterval(statusInterval);
      alert('Generation is taking longer than expected. Please check your library in a few minutes.');
      setGenerating(false);
    }, 600000);
  };

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);

  return (
    <>
      <div style={{
        minHeight: '70vh',
        background: 'linear-gradient(180deg, #FFF9F5 0%, #FFF5F0 30%, #FFEFE8 70%, #FFE8E0 100%)',
        paddingTop: '0px',
        paddingBottom: '30px',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>

          {/* Admin Badge - Shows when admin user is logged in */}
          {isAdminUser && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '1rem',
              paddingBottom: '0.5rem',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #F59E0B, #EF4444)',
                color: 'white',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
              }}>
                <Shield size={16} />
                <span>ADMIN MODE - Can generate up to 50 pages</span>
              </div>
            </div>
          )}


          {/* Progress Header */}
          <SoftGlow color="#C9B6E4" intensity={0.2}>
            <div 
              className="progress-header-scroll"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 'var(--r-2xl, 28px)',
                padding: '1.5rem',
                marginBottom: '3rem',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                overflowX: 'auto',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                justifyContent: 'space-between',
                minWidth: 'fit-content',
              }}>
                {STEPS.map((step, index) => {
                  const isActive = step.id === currentStep;
                  const isCompleted = index < currentStepIndex;
                  const canNavigate = isCompleted || isActive;
                  
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Step clicked:', step.id, 'canNavigate:', canNavigate, 'isActive:', isActive, 'isCompleted:', isCompleted);
                        if (canNavigate) {
                          setCurrentStep(step.id);
                        }
                      }}
                      disabled={!canNavigate}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flex: '1',
                        minWidth: '80px',
                        position: 'relative',
                        background: 'none',
                        border: 'none',
                        cursor: canNavigate ? 'pointer' : 'not-allowed',
                        padding: 0,
                      }}
                    >
                      <div
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: isActive 
                            ? 'linear-gradient(135deg, #C5B4FF 0%, #B5F2E0 100%)'
                            : isCompleted 
                            ? 'linear-gradient(135deg, #A8D8E6 0%, #7DD8E8 100%)' 
                            : 'rgba(224, 213, 240, 0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: isActive 
                            ? '0 8px 32px rgba(197, 180, 255, 0.5), inset 0 2px 8px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3)'
                            : isCompleted
                            ? '0 4px 16px rgba(168, 216, 230, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.3)'
                            : '0 2px 8px rgba(0, 0, 0, 0.08)',
                          opacity: canNavigate ? 1 : 0.5,
                          pointerEvents: 'auto',
                          backdropFilter: 'blur(10px)',
                          border: isActive 
                            ? '2px solid rgba(197, 180, 255, 0.4)' 
                            : isCompleted 
                            ? '2px solid rgba(168, 216, 230, 0.3)'
                            : '2px solid rgba(224, 213, 240, 0.3)',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                        onMouseEnter={(e) => {
                          if (canNavigate && !isActive) {
                            e.currentTarget.style.transform = 'scale(1.15) translateY(-4px)';
                            e.currentTarget.style.boxShadow = isCompleted
                              ? '0 12px 40px rgba(168, 216, 230, 0.4), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 2px rgba(168, 216, 230, 0.4)'
                              : '0 8px 24px rgba(197, 180, 255, 0.35), inset 0 2px 12px rgba(255, 255, 255, 0.3), 0 0 0 2px rgba(197, 180, 255, 0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (canNavigate) {
                            e.currentTarget.style.transform = 'scale(1) translateY(0)';
                            e.currentTarget.style.boxShadow = isActive 
                              ? '0 8px 32px rgba(197, 180, 255, 0.5), inset 0 2px 8px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3)'
                              : isCompleted
                              ? '0 4px 16px rgba(168, 216, 230, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.3)'
                              : '0 2px 8px rgba(0, 0, 0, 0.08)';
                          }
                        }}
                        aria-label={`${isCompleted ? 'Go back to' : ''} ${step.label} step`}
                      >
                        {/* Shimmer effect for active state */}
                        {isActive && (
                          <div style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent)',
                            pointerEvents: 'none',
                            animation: 'shimmer 2s ease-in-out infinite',
                          }} />
                        )}
                        
                        {/* Glow pulse for active state */}
                        {isActive && (
                          <div style={{
                            position: 'absolute',
                            inset: '-4px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(197, 180, 255, 0.4), rgba(181, 242, 224, 0.4))',
                            filter: 'blur(12px)',
                            opacity: 0.7,
                            animation: 'gentle-pulse 2s ease-in-out infinite',
                            pointerEvents: 'none',
                          }} />
                        )}
                        
                        {isCompleted ? (
                          <Check 
                            size={28} 
                            strokeWidth={3} 
                            style={{
                              color: 'white',
                              position: 'relative',
                              zIndex: 1,
                              filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                            }}
                          />
                        ) : step.id === 'idea' ? (
                          <Lightbulb 
                            size={28} 
                            strokeWidth={2.5} 
                            style={{
                              color: isActive ? 'white' : '#9ca3af',
                              position: 'relative',
                              zIndex: 1,
                              filter: isActive ? 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))' : 'none',
                            }}
                          />
                        ) : step.id === 'language' ? (
                          <Globe2 
                            size={28} 
                            strokeWidth={2.5} 
                            style={{
                              color: isActive ? 'white' : '#9ca3af',
                              position: 'relative',
                              zIndex: 1,
                              filter: isActive ? 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))' : 'none',
                            }}
                          />
                        ) : (
                          <Mic 
                            size={28} 
                            strokeWidth={2.5} 
                            style={{
                              color: isActive ? 'white' : '#9ca3af',
                              position: 'relative',
                              zIndex: 1,
                              filter: isActive ? 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))' : 'none',
                            }}
                          />
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 700 : 600,
                        color: isActive ? '#7C3AED' : isCompleted ? '#444' : '#888',
                        textAlign: 'center',
                      }}>
                        {step.label}
                      </div>
                      
                      {/* Connector Line */}
                      {index < STEPS.length - 1 && (
                        <div style={{
                          position: 'absolute',
                          top: '24px',
                          left: 'calc(50% + 24px)',
                          width: 'calc(100% - 48px)',
                          height: '3px',
                          background: isCompleted ? '#C5B4FF' : '#e0d5f0',
                          transition: 'all 0.3s ease',
                          pointerEvents: 'none',
                        }} />
                      )}
                    </button>
                  );
                })}
                
                {/* Visual ✨ indicator (not a clickable step) */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flex: '1',
                  minWidth: '80px',
                }}>
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      background: generating 
                        ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                        : 'rgba(224, 213, 240, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: generating 
                        ? '0 8px 32px rgba(16, 185, 129, 0.5), inset 0 2px 8px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.08)',
                      opacity: generating ? 1 : 0.4,
                      backdropFilter: 'blur(10px)',
                      border: generating 
                        ? '2px solid rgba(16, 185, 129, 0.4)' 
                        : '2px solid rgba(224, 213, 240, 0.3)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    aria-live="polite"
                    aria-label={generating ? "Generating your audio book..." : "Generate step indicator"}
                  >
                    {/* Shimmer effect for generating state */}
                    {generating && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent)',
                        pointerEvents: 'none',
                        animation: 'shimmer 2s ease-in-out infinite',
                      }} />
                    )}
                    
                    {/* Glow pulse for generating state */}
                    {generating && (
                      <div style={{
                        position: 'absolute',
                        inset: '-4px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.4), rgba(52, 211, 153, 0.4))',
                        filter: 'blur(12px)',
                        opacity: 0.7,
                        animation: 'gentle-pulse 2s ease-in-out infinite',
                        pointerEvents: 'none',
                      }} />
                    )}
                    
                    <Sparkles 
                      size={28} 
                      strokeWidth={2.5} 
                      style={{
                        color: generating ? 'white' : '#9ca3af',
                        position: 'relative',
                        zIndex: 1,
                        filter: generating ? 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))' : 'none',
                        animation: generating ? 'rotate 3s linear infinite' : 'none',
                      }}
                    />
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: generating ? 700 : 600,
                    color: generating ? '#10b981' : '#888',
                    textAlign: 'center',
                  }}>
                    Generate
                  </div>
                </div>
              </div>
            </div>
          </SoftGlow>

          {/* Step Content */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 'var(--r-2xl, 28px)',
            padding: 'clamp(1.5rem, 3vw, 2.5rem)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            marginBottom: '1.5rem',
            minHeight: '500px',
          }}>
            {currentStep === 'idea' && (
              <StepIdea
                value={wizardState.idea}
                onChange={(value) => setWizardState(prev => ({ ...prev, idea: value }))}
                onQuickGenerate={() => setCurrentStep('language')}
                montessoriMode={wizardState.montessoriMode}
                onMontessoriModeChange={(enabled) => setWizardState(prev => ({ ...prev, montessoriMode: enabled }))}
                isSubscriber={isSubscriber}
              />
            )}

            {currentStep === 'language' && (
              <StepLanguage
                primaryLanguage={wizardState.primaryLanguage}
                onPrimaryLanguageChange={(lang) => setWizardState(prev => ({ ...prev, primaryLanguage: lang }))}
                bilingualMode={wizardState.bilingualMode}
                onBilingualModeChange={(enabled) => setWizardState(prev => ({ ...prev, bilingualMode: enabled }))}
                secondaryLanguage={wizardState.secondaryLanguage}
                onSecondaryLanguageChange={(lang) => setWizardState(prev => ({ ...prev, secondaryLanguage: lang }))}
                montessoriMode={wizardState.montessoriMode}
                onMontessoriModeChange={(enabled) => setWizardState(prev => ({ ...prev, montessoriMode: enabled }))}
                isSubscriber={isSubscriber}
                isPro={isPro}
                onNext={() => setCurrentStep('voice')}
              />
            )}

          {currentStep === 'voice' && (
            <>
              <StepVoice
                value={wizardState.voiceId}
                onChange={(voiceId) => {
                  setWizardState(prev => ({ ...prev, voiceId }));
                  // Optionally fetch voice name
                  fetch('/api/voices').then(async (r) => {
                    if (r.ok) {
                      const data = await r.json();
                      const voice = data.voices.find((v: any) => v.voiceId === voiceId);
                      if (voice) setVoiceName(voice.name);
                    }
                  });
                }}
                isSubscriber={isSubscriber}
                isPro={isPro}
                language={wizardState.primaryLanguage}
              />

              {/* Admin Page Count Control - Shows for admin users only */}
              {isAdminUser && wizardState.voiceId && !generating && (
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #FEF3C7, #FED7AA)',
                  border: '2px solid #F59E0B',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                  }}>
                    <Shield size={24} color="#F59E0B" />
                    <h3 style={{ margin: 0, color: '#92400E', fontSize: '1.1rem', fontWeight: 700 }}>
                      Admin: Custom Page Count
                    </h3>
                  </div>
                  
                  <p style={{ color: '#92400E', marginBottom: '1rem', fontSize: '0.95rem', lineHeight: 1.5 }}>
                    For YouTube videos, you can generate up to 50 pages (default is 12 pages for regular users). Longer stories create better video content!
                  </p>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                      display: 'block',
                      color: '#92400E',
                      fontWeight: 600,
                      marginBottom: '0.5rem',
                      fontSize: '1rem',
                    }}>
                      Page Count: {wizardState.pageCount || 12} pages
                    </label>
                    <input
                      type="range"
                      min="12"
                      max="50"
                      value={wizardState.pageCount || 12}
                      onChange={(e) => setWizardState(prev => ({
                        ...prev,
                        pageCount: parseInt(e.target.value)
                      }))}
                      style={{
                        width: '100%',
                        height: '10px',
                        borderRadius: '5px',
                        appearance: 'none',
                        background: 'linear-gradient(90deg, #F59E0B, #EF4444)',
                        cursor: 'pointer',
                        outline: 'none',
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.85rem',
                      color: '#92400E',
                      marginTop: '0.5rem',
                      fontWeight: 500,
                    }}>
                      <span>12 pages (Normal)</span>
                      <span>50 pages (YouTube)</span>
                    </div>
                  </div>
                  
                  <div style={{
                    padding: '0.75rem 1rem',
                    background: 'rgba(255,255,255,0.7)',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: '#92400E',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>💡</span>
                    <span>
                      <strong>Tip:</strong> 50 pages ≈ 10-15 minute video, perfect for YouTube! Same 3 credit cost.
                    </span>
                  </div>
                </div>
              )}

              {/* Generate Button - Shows when voice is selected */}
              {wizardState.voiceId && !generating && (
                <div style={{
                  marginTop: '3rem',
                  paddingTop: '2rem',
                  borderTop: '2px solid #f0f0f0',
                  textAlign: 'center',
                }}>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={generating || !creditsLoaded}
                    className="premium-generate-btn"
                    style={{
                      width: '100%',
                      maxWidth: '520px',
                      padding: '1.625rem 3rem',
                      background: (generating || !creditsLoaded) 
                        ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 50%, #4b5563 100%)' 
                        : 'linear-gradient(135deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '28px',
                      fontSize: '1.375rem',
                      fontWeight: 900,
                      cursor: (generating || !creditsLoaded) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '1rem',
                      margin: '0 auto',
                      boxShadow: (generating || !creditsLoaded)
                        ? '0 12px 40px rgba(107, 114, 128, 0.4), inset 0 2px 12px rgba(255, 255, 255, 0.15), 0 0 0 2px rgba(255, 255, 255, 0.1)'
                        : '0 12px 40px rgba(16, 185, 129, 0.5), inset 0 2px 12px rgba(255, 255, 255, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      opacity: (generating || !creditsLoaded) ? 0.75 : 1,
                      letterSpacing: '0.01em',
                    }}
                    onMouseEnter={(e) => {
                      if (!generating && creditsLoaded) {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 16px 56px rgba(16, 185, 129, 0.6), inset 0 2px 16px rgba(255, 255, 255, 0.4), 0 0 0 3px rgba(255, 255, 255, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!generating && creditsLoaded) {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(16, 185, 129, 0.5), inset 0 2px 12px rgba(255, 255, 255, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.2)';
                      }
                    }}
                  >
                    {/* Shimmer overlay - active state only */}
                    {!generating && creditsLoaded && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        animation: 'shimmer 3s ease-in-out infinite',
                        pointerEvents: 'none',
                      }} />
                    )}

                    {/* Pulse overlay - loading state only */}
                    {(generating || !creditsLoaded) && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                        animation: 'shimmer 2s ease-in-out infinite',
                        pointerEvents: 'none',
                      }} />
                    )}
                    
                    {!creditsLoaded ? (
                      <>
                        <span style={{ 
                          fontSize: '2rem', 
                          position: 'relative', 
                          zIndex: 1,
                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                          animation: 'spin 2s linear infinite',
                        }}>⏳</span>
                        <span style={{ 
                          position: 'relative', 
                          zIndex: 1,
                          textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        }}>Loading...</span>
                      </>
                    ) : (
                      <>
                        <span style={{ 
                          fontSize: '2rem', 
                          position: 'relative', 
                          zIndex: 1,
                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                        }}>🎨</span>
                        <span style={{ 
                          position: 'relative', 
                          zIndex: 1,
                          textShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        }}>Generate Audio Picture Book</span>
                        <span style={{ 
                          fontSize: '2rem', 
                          position: 'relative', 
                          zIndex: 1,
                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                        }}>✨</span>
                      </>
                    )}
                  </button>
                  
                  <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2rem',
                    flexWrap: 'wrap',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#666',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                    }}>
                      <span style={{ fontSize: '1.25rem' }}>💳</span>
                      <span>3 credits</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#666',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                    }}>
                      <span style={{ fontSize: '1.25rem' }}>⏱️</span>
                      <span>~2-3 minutes</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#666',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                    }}>
                      <span style={{ fontSize: '1.25rem' }}>🎧</span>
                      <span>Professional narration</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
        </div>
      </div>

      {/* Generation Progress Modal */}
      <MagicalGenerationModal
        isGenerating={generating}
        progress={progress}
        currentStep={currentStepLabel}
        videoUrl="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/loading-audio-book.mp4"
      />

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        onPurchase={handlePurchase}
        onSubscribe={handleSubscribe}
      />

      {/* Purchase Success Modal */}
      {purchaseDetails && (
        <PurchaseSuccessModal
          isOpen={showPurchaseSuccess}
          onClose={() => {
            setShowPurchaseSuccess(false);
            setPurchaseDetails(null);
          }}
          purchaseType={purchaseDetails.type}
          amount={purchaseDetails.amount}
        />
      )}

      {/* Styles for scrollbar hiding and animations */}
      <style jsx>{`
        .progress-header-scroll {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }

        .progress-header-scroll::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

